import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { DraftBlog, EntryDraft } from './types';
import { useEntryDraftModel } from './useEntryDraftModel';

const EntryDraftContext = createContext<EntryDraft | undefined>(undefined);
EntryDraftContext.displayName = 'EntryDraftContext';

interface Props {
  /** '0' for a new entry. */
  entryId: string;
  blog: DraftBlog;
  children: ReactNode;
}

/**
 * Owns the draft of the entry being edited: reading, saving and clearing it.
 * Keep it mounted for the whole edit session (e.g. across the preview).
 *
 * It knows nothing about the editor: feed it `{title, content, tags}` through
 * `useEntryDraft().save`, with `content` as html.
 */
export function EntryDraftProvider({ entryId, blog, children }: Props) {
  const draft = useEntryDraftModel({ entryId, blog });

  return (
    <EntryDraftContext.Provider value={draft}>
      {children}
    </EntryDraftContext.Provider>
  );
}

export function useEntryDraft(): EntryDraft {
  const draft = useContext(EntryDraftContext);

  if (draft === undefined) {
    throw new Error('useEntryDraft must be used within an EntryDraftProvider');
  }

  return draft;
}
